import React, { useState, useRef, useEffect } from 'react';
import { palette } from '@/styles/figma-tokens';
import Modal from '../../../../components/common/Modal';
import DropZone from '../shared/DropZone';

interface AddSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** imageRemoved is true only in edit mode when the user cleared an existing image. */
  onCreateSection: (title: string, imageFile: File | null, imageRemoved: boolean) => void;
  creating?: boolean;
  /** 'edit' pre-fills the title + existing image and relabels the modal. */
  mode?: 'create' | 'edit';
  initialTitle?: string;
  initialImageUrl?: string | null;
}

/** Pencil / edit icon */
function EditIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15.232 5.232l3.536 3.536M16.707 3.293a2 2 0 012.828 0l1.172 1.172a2 2 0 010 2.828L7.5 20.5H3v-4.5L16.707 3.293z"
        stroke={palette.grey['600']}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AddSectionModal({
  isOpen,
  onClose,
  onCreateSection,
  creating,
  mode = 'create',
  initialTitle = '',
  initialImageUrl = null,
}: AddSectionModalProps) {
  const [title, setTitle] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // In edit mode: true once the user clears the pre-existing image.
  const [imageRemoved, setImageRemoved] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Cleanup blob URLs on unmount (revokeObjectURL is a no-op on remote URLs).
  useEffect(() => {
    return () => { if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  // Reset internal state every time the modal opens. In edit mode, seed from
  // the section's current title + image so the user edits from where it is.
  useEffect(() => {
    if (!isOpen) return;
    setTitle(mode === 'edit' ? initialTitle : '');
    setError('');
    setImageFile(null);
    setImageRemoved(false);
    setImagePreview((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return mode === 'edit' ? initialImageUrl : null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleImageFiles = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setError('Only JPEG, PNG, WebP, or GIF images are allowed.');
      return;
    }
    setError('');
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImageRemoved(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImageRemoved(true);
    setImagePreview(null);
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      setError('Section name is required.');
      return;
    }
    onCreateSection(title.trim(), imageFile, imageRemoved);
  };

  const handleClose = () => {
    if (creating) return;
    removeImage();
    setTitle('');
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={mode === 'edit' ? 'Edit Section' : 'New Section'} size="md">
      <div style={{ padding: '0 4px' }}>

        {/* ── Title input — rounded pill with pencil icon ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '28px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1.5px solid rgba(189,188,201,0.5)',
              borderRadius: '999px',
              padding: '0 16px',
              height: '44px',
              width: '100%',
              maxWidth: '380px',
              transition: 'border-color 0.15s',
            }}
          >
            <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <EditIcon />
            </span>
            <input
              ref={inputRef}
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setError(''); }}
              placeholder="Enter the name of the section"
              autoFocus
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                color: 'rgb(var(--color-text-primary))',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: '0',
                width: '100%',
              }}
            />
          </div>
        </div>

        {/* ── Header image ── */}
        <div style={{ marginBottom: '8px' }}>
          <h3
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              textAlign: 'center',
              color: 'rgb(var(--color-text-primary))',
              marginBottom: '16px',
            }}
          >
            Upload the Header Picture for This Section
          </h3>

          {imagePreview ? (
            <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden' }}>
              <img
                src={imagePreview}
                alt="Section header preview"
                style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }}
              />
              <button
                onClick={removeImage}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1 1l10 10M11 1L1 11" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          ) : (
            <DropZone
              onFilesSelected={handleImageFiles}
              accept="image/jpeg,image/png,image/webp,image/gif"
              maxSizeMB={5}
              label="Drop image here"
            />
          )}
        </div>

        {error && (
          <p
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '12px',
              color: palette.error['500'],
              marginTop: '8px',
            }}
            role="alert"
          >
            {error}
          </p>
        )}

        {/* ── Footer ── */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '20px',
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={creating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 20px',
              borderRadius: '500px',
              border: '1.5px solid rgb(var(--color-secondary) / 0.4)',
              background: 'transparent',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '13px',
              fontWeight: 700,
              color: 'rgb(var(--color-secondary))',
              cursor: creating ? 'not-allowed' : 'pointer',
              opacity: creating ? 0.4 : 1,
              transition: 'background-color 0.15s, border-color 0.15s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!title.trim() || creating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 20px',
              borderRadius: '500px',
              border: 'none',
              background: !title.trim() || creating ? 'rgb(var(--color-primary) / 0.5)' : 'rgb(var(--color-primary))',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '13px',
              fontWeight: 700,
              color: '#fff',
              cursor: !title.trim() || creating ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {creating ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
